import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AppService } from '../../../app.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { SystemMessageService } from './system-message.service';
import { createGtwAppkitTextEditorConfig } from '../../../shared/_config/gtw-appkit-text-editor.config';

interface UserType {
  name: string;
  value: number;
}

interface NotifClient {
  client_key: number | string;
  client_name: string;
}

interface NotifLookup {
  LABEL?: string;
  VALUE?: string | number;
  label?: string;
  value?: string | number;
}

/**
 * Admin → System Message screen.
 *
 * Migrated from the legacy AngularJS controller `systemMessageCtrl`
 * (`GTW12/ui/app/components/admin/systemMessage/systemMessageCtrl.js`),
 * service `systemMessageFactory` and CKEditor directive `messageCkeditor`.
 *
 * Architecture notes (see `ADMIN_TAB_MIGRATION_GUIDE.md` §3.5, §7, §8):
 *  - No grid → no `AdminGridDirective` and no `AdminScreenContextService`.
 *  - No modal → no `entryComponents` registration.
 *  - All data sources injected via services (no `@Input` setters from the host).
 *  - Rich-text editing uses the AppKit4 `<ap-text-editor>` wrapper
 *    (`@appkit4/angular-text-editor`) — same component used by
 *    `release-notes.component`. The legacy directive `messageCkeditor`
 *    mounted CKEditor 5 manually; the AppKit4 wrapper bundles the
 *    same editor with its CSS and lifecycle so we no longer need to.
 */
@Component({
  selector: 'gtw-system-message',
  templateUrl: './system-message.component.html',
  styleUrls: ['./system-message.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SystemMessageComponent implements OnInit, OnDestroy {
  readonly title = 'System Message';

  /** AppKit4 ap-text-editor (wrapped CKEditor 5) — same usage as release-notes. */
  notifMsgBody = '';
  readonly editorConfig = createGtwAppkitTextEditorConfig('Enter system message body…');

  /** Header / left rail state. */
  notifSelectedTypeVal: 'system' | 'client' = 'system';
  selectedMsgFilterVal: 'show all' | 'archive' = 'show all';
  searchNotif = '';
  showNotifLoader = false;

  /** Right panel state. */
  showSystemMessageContent = false;
  createSystemMessageHdr = '';
  submitted = false;
  customValidations = false;
  saving = false;

  /** Client selector (only shown when notifSelectedTypeVal === 'client'). */
  clients: NotifClient[] = [];
  selectedClient: NotifClient | null = null;

  /** User typeahead state. */
  userList: any[] = [];
  selectedUserList: any[] = [];
  searchUserText = '';
  searchResults: any[] = [];

  /** Notification data. */
  systemNotificationTotalList: any[] = [];
  systemNotificationList: any[] = [];
  systemNotificationListByMsgFilter: any[] = [];

  /** Reference data + selections. */
  taxTypeList: NotifLookup[] = [];
  appKeyList: NotifLookup[] = [];
  dropdownLists: Record<'appKey' | 'taxType' | 'client', any[]> = {
    appKey: [],
    taxType: [],
    client: [],
  };
  taxTypeSelected: NotifLookup[] = [];
  appKeySelected: NotifLookup[] = [];
  clientListSelected: NotifClient[] = [];

  selectedSysMsgData: any = {};

  /** Hardcoded permission levels (1..5) — same labels as legacy. */
  readonly userTypes: UserType[] = [
    { name: 'User', value: 1 },
    { name: 'Editor', value: 2 },
    { name: 'Admin', value: 3 },
    { name: 'Developer', value: 4 },
    { name: 'Super Admin', value: 5 },
  ];
  userTypeValue: number | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private appService: AppService,
    private snackbar: GtwSnackbarService,
    private systemMessageService: SystemMessageService,
  ) {}

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  ngOnInit(): void {
    this.initClientData();
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------------------------------------------------------------------
  // Init helpers
  // ---------------------------------------------------------------------------

  private initClientData(): void {
    const userClients = this.appService.USER_SETTINGS?.user?.clients;
    this.clients = this.toAppkitOptions(Array.isArray(userClients) ? userClients : []);
    this.dropdownLists.client = [...this.clients];
  }

  private loadInitialData(): void {
    this.showNotifLoader = true;
    this.cdr.detectChanges();

    this.systemMessageService.getAllUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (usersData: any) => {
          this.userList = usersData?.jsonObject || [];
          this.loadSystemNotifications();
        },
        error: () => {
          this.showNotifLoader = false;
          this.snackbar.openSnackBar('Failed to load users list.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  private loadSystemNotifications(): void {
    this.systemMessageService.getAllSystemNotif()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (notifData: any) => {
          this.systemNotificationTotalList = notifData?.system_notif_list || [];
          this.appKeyList = this.toLookupOptions(notifData?.app_list || []);
          this.taxTypeList = this.toLookupOptions(notifData?.tax_type_list || []);
          this.dropdownLists.appKey = [...this.appKeyList];
          this.dropdownLists.taxType = [...this.taxTypeList];
          this.applyTypeFilter();
          this.showNotifLoader = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.showNotifLoader = false;
          this.snackbar.openSnackBar('Failed to load system notifications.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  onDropdownFilterResult(
    key: 'appKey' | 'taxType' | 'client',
    event: any,
  ): void {
    this.dropdownLists[key] = event?.result || [];
  }

  private toLookupOptions(items: NotifLookup[]): any[] {
    return this.toAppkitOptions(items).map((item: NotifLookup) => ({
      ...item,
      __dropdownKey: item.VALUE ?? item.value ?? item.LABEL ?? item.label,
    }));
  }

  private toAppkitOptions(items: any[]): any[] {
    return (items || []).map((item: any) => ({
      ...item,
      showCheckbox: true,
      role: 'checkbox',
    }));
  }

  private stripDropdownMetadata(item: any): any {
    const data = { ...(item || {}) };
    delete data.showCheckbox;
    delete data.role;
    delete data.__dropdownKey;
    return data;
  }

  /**
   * Template-friendly check for the body validation error. Returns `true`
   * when the editor is empty / contains only whitespace. Safe to call from
   * `*ngIf` because it does not mutate state.
   */
  isBodyEmpty(): boolean {
    return !String(this.notifMsgBody || '').replace(/<[^>]*>/g, '').trim();
  }

  // ---------------------------------------------------------------------------
  // Notification type / client filters
  // ---------------------------------------------------------------------------

  setNotifType(type: 'system' | 'client'): void {
    if (this.notifSelectedTypeVal === type) return;
    this.notifSelectedTypeVal = type;
    if (type === 'system') {
      this.selectedClient = null;
    }
    this.applyTypeFilter();
  }

  changeNotifType(): void {
    this.applyTypeFilter();
  }

  changeClientType(): void {
    this.applyTypeFilter();
  }

  messageFilter(): void {
    this.showSystemMessageContent = false;
    if (this.selectedMsgFilterVal === 'archive') {
      this.systemNotificationListByMsgFilter = this.systemNotificationList
        .filter((item: any) => item.notif_status === 'closed');
    } else {
      this.systemNotificationListByMsgFilter = this.systemNotificationList
        .filter((item: any) => item.notif_status === 'open');
    }
    this.cdr.detectChanges();
  }

  /**
   * Re-derive `systemNotificationList` from `systemNotificationTotalList`
   * based on the current Notification Type / Client selection.
   *
   * Pitfall 9: `client_key` may legitimately be `0` (system tenant), so the
   * "any client" branch tests for `null` / empty-string explicitly.
   */
  private applyTypeFilter(): void {
    if (this.notifSelectedTypeVal === 'client') {
      const clientKey = this.selectedClient?.client_key;
      this.systemNotificationList = (this.systemNotificationTotalList || []).filter((item: any) => {
        if (item.notif_type !== 'client') return false;
        if (clientKey == null || clientKey === '') {
          // No client selected → show all client-typed messages (including
          // those tagged with empty client_keys).
          return true;
        }
        return String(item.client_keys || '').indexOf(String(clientKey)) > -1;
      });
    } else {
      this.systemNotificationList = (this.systemNotificationTotalList || [])
        .filter((item: any) => item.notif_type === 'system');
    }
    this.messageFilter();
  }

  // ---------------------------------------------------------------------------
  // Create / Edit / Reset
  // ---------------------------------------------------------------------------

  createNotifListData(): void {
    this.createSystemMessageHdr = 'Create New';
    this.showSystemMessageContent = true;
    this.submitted = false;
    this.customValidations = false;
    this.selectedSysMsgData = {};
    this.taxTypeSelected = [];
    this.appKeySelected = [];
    this.clientListSelected = [];
    this.selectedUserList = [];
    this.userTypeValue = null;
    this.notifMsgBody = '';

    // Clear the "selected" flag on left-rail rows.
    this.systemNotificationListByMsgFilter.forEach((row: any) => (row.selected = false));
    this.cdr.detectChanges();
  }

  editNotifListData(list: any): void {
    this.createSystemMessageHdr = 'Edit';
    this.showSystemMessageContent = true;
    this.submitted = false;
    this.customValidations = false;
    this.selectedSysMsgData = { ...list };

    // Highlight the selected row.
    this.systemNotificationListByMsgFilter.forEach((row: any) => {
      row.selected = row.notif_msg_key === list.notif_msg_key;
    });

    // App Key / Tax Type — API stores them as JSON strings.
    this.appKeySelected = this.toLookupOptions(
      this.safeParseArray(this.selectedSysMsgData?.app_key),
    );
    this.taxTypeSelected = this.toLookupOptions(
      this.safeParseArray(this.selectedSysMsgData?.tax_type),
    );

    // User type.
    this.userTypeValue = this.selectedSysMsgData?.notif_user_type != null
      ? Number(this.selectedSysMsgData.notif_user_type)
      : null;

    // Selected user chips.
    const userIds = String(this.selectedSysMsgData?.notif_user_list || '')
      .split(',')
      .map((s: string) => s.trim())
      .filter((s: string) => !!s);
    this.selectedUserList = (this.userList || [])
      .filter((u: any) => userIds.includes(String(u.SSO_ID)));

    // Selected client chips (client-type only).
    const clientKeys = String(this.selectedSysMsgData?.client_keys || '')
      .split(',')
      .map((s: string) => s.trim())
      .filter((s: string) => !!s);
    this.clientListSelected = (this.clients || [])
      .filter((c: any) => clientKeys.includes(String(c.client_key)));

    // Dates — convert to yyyy-mm-dd for <input type="date">.
    this.selectedSysMsgData.notif_start_date = this.toIsoDate(this.selectedSysMsgData?.notif_start_date);
    this.selectedSysMsgData.notif_end_date = this.toIsoDate(this.selectedSysMsgData?.notif_end_date);

    // Body — JSON.stringified HTML in the API.
    this.notifMsgBody = this.safeParseString(this.selectedSysMsgData?.notif_msg_body);

    this.cdr.detectChanges();
  }

  // ---------------------------------------------------------------------------
  // Archive / Save
  // ---------------------------------------------------------------------------

  archiveList(item: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const nextStatus = item?.notif_status === 'closed' ? 'open' : 'closed';
    const payload = {
      ...item,
      notif_start_date: this.toServerDate(item?.notif_start_date),
      notif_end_date: this.toServerDate(item?.notif_end_date),
      notif_status: nextStatus,
    };
    this.persistPayload(payload, true);
  }

  saveDetails(): void {
    this.submitted = true;
    this.customValidations = true;
    this.cdr.detectChanges();

    if (!this.isFormValid()) {
      this.snackbar.openSnackBar('Please correct the errors below.', 'gtw-snackbar--yellow');
      return;
    }

    const payload = this.buildSavePayload();
    this.persistPayload(payload, false);
  }

  private buildSavePayload(): any {
    return {
      notif_msg_key: this.selectedSysMsgData?.notif_msg_key,
      notif_msg_title: this.selectedSysMsgData?.notif_msg_title,
      notif_msg_body: JSON.stringify(this.notifMsgBody || ''),
      business_rights: '',
      notif_user_list: (this.selectedUserList || []).map((u: any) => u.SSO_ID).join(','),
      tax_type: JSON.stringify(
        (this.taxTypeSelected || []).map((item) => this.stripDropdownMetadata(item)),
      ),
      notif_user_type: this.userTypeValue,
      app_key: JSON.stringify(
        (this.appKeySelected || []).map((item) => this.stripDropdownMetadata(item)),
      ),
      notif_start_date: this.toServerDate(this.selectedSysMsgData?.notif_start_date),
      notif_end_date: this.toServerDate(this.selectedSysMsgData?.notif_end_date),
      // Legacy contract: `999999999` denotes "all clients" for system-wide
      // messages; client messages send a comma-separated client_keys CSV.
      client_keys: this.notifSelectedTypeVal === 'system'
        ? '999999999'
        : (this.clientListSelected || []).map((c: any) => c.client_key).join(','),
      notif_status: this.selectedSysMsgData?.notif_status || 'open',
      notif_type: this.notifSelectedTypeVal,
    };
  }

  private persistPayload(payload: any, isArchive: boolean): void {
    this.saving = !isArchive;
    this.cdr.detectChanges();

    this.systemMessageService.saveSysMsg([payload])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.saving = false;
          if (String(response?.callSuccess) === '1') {
            this.snackbar.openSnackBar(
              isArchive
                ? 'Notification status updated successfully.'
                : (payload?.notif_msg_key
                    ? 'System notification updated successfully.'
                    : 'System notification created successfully.'),
              'gtw-snackbar--green',
            );
            this.loadSystemNotifications();
          } else {
            this.snackbar.openSnackBar(
              response?.errorMessage || 'Save failed.',
              'gtw-snackbar--red',
            );
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.saving = false;
          this.snackbar.openSnackBar(
            'Error occurred while saving system message.',
            'gtw-snackbar--red',
          );
          this.cdr.detectChanges();
        },
      });
  }

  private isFormValid(): boolean {
    if (!this.selectedSysMsgData?.notif_start_date) return false;
    if (!this.selectedSysMsgData?.notif_end_date) return false;
    if (!String(this.selectedSysMsgData?.notif_msg_title || '').trim()) return false;
    if (this.isBodyEmpty()) return false;
    if (this.notifSelectedTypeVal === 'client' && (this.clientListSelected || []).length === 0) {
      return false;
    }
    return true;
  }

  // ---------------------------------------------------------------------------
  // User typeahead
  // ---------------------------------------------------------------------------

  searchUserList(): void {
    const term = (this.searchUserText || '').trim();
    if (term.length < 3) {
      this.searchResults = [];
      this.cdr.detectChanges();
      return;
    }
    const lower = term.toLowerCase();
    this.searchResults = (this.userList || []).filter((u: any) => {
      const name = String(u.DISPLAY_NAME || '').toLowerCase();
      const sso = String(u.SSO_ID || '');
      return name.includes(lower) || sso.includes(term);
    });
    this.cdr.detectChanges();
  }

  selectedUser(user: any): void {
    const exists = this.selectedUserList
      .some((u: any) => String(u.SSO_ID) === String(user.SSO_ID));
    if (!exists) {
      this.selectedUserList = [...this.selectedUserList, user];
    }
    this.searchUserText = '';
    this.searchResults = [];
    this.cdr.detectChanges();
  }

  deleteUser(index: number): void {
    this.selectedUserList = this.selectedUserList.filter((_u, i) => i !== index);
    this.cdr.detectChanges();
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  /**
   * Track-by for the left rail. We use `notif_msg_key` when available
   * (existing rows) and fall back to the index for transient placeholders.
   */
  trackByMsgKey(_index: number, item: any): any {
    return item?.notif_msg_key ?? _index;
  }

  compareLookup(a: NotifLookup, b: NotifLookup): boolean {
    if (!a || !b) return a === b;
    const av = a.VALUE ?? a.value;
    const bv = b.VALUE ?? b.value;
    return String(av) === String(bv);
  }

  compareClient(a: NotifClient, b: NotifClient): boolean {
    if (!a || !b) return a === b;
    return String(a.client_key) === String(b.client_key);
  }

  lookupLabel(item: NotifLookup): string {
    return String(item?.LABEL ?? item?.label ?? item?.VALUE ?? item?.value ?? '');
  }

  filteredLeftList(): any[] {
    const term = (this.searchNotif || '').toLowerCase().trim();
    if (!term) return this.systemNotificationListByMsgFilter;
    return this.systemNotificationListByMsgFilter
      .filter((item: any) => String(item?.notif_msg_title || '').toLowerCase().includes(term));
  }

  private safeParseArray(value: any): NotifLookup[] {
    if (!value) return [];
    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * Body is persisted by the legacy controller via `JSON.stringify(getData())`,
   * so a freshly-loaded value is an HTML string wrapped in JSON quotes
   * (`"<p>hello</p>"`). Older records may have been saved as bare HTML.
   */
  private safeParseString(value: any): string {
    if (!value) return '';
    if (typeof value !== 'string') return String(value);
    const trimmed = value.trim();
    if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return trimmed.slice(1, -1);
      }
    }
    return value;
  }

  /** Converts whatever date shape we receive into `yyyy-mm-dd`. */
  private toIsoDate(value: any): string {
    if (!value) return '';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.substring(0, 10);
    }
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  /** Converts the `<input type="date">` value back to a Date the API expects. */
  private toServerDate(value: any): any {
    if (!value) return null;
    if (value instanceof Date) return value;
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : d;
  }
}
